import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BENEFIT_ICON_KEYS } from "../../lib/benefitIcons";
import { createBook, updateBook } from "../../lib/booksApi";

const DEFAULT_BENEFITS = [
  { title: "", description: "" },
  { title: "", description: "" },
  { title: "", description: "" },
  { title: "", description: "" },
];

// mode: "create" | "edit". book is required for "edit".
export default function BookForm({ mode, book }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [benefits, setBenefits] = useState(
    book?.benefits?.length ? book.benefits : DEFAULT_BENEFITS
  );

  function updateBenefit(index, field, value) {
    setBenefits((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [field]: value } : b))
    );
  }

  function addBenefit() {
    setBenefits((prev) => [...prev, { title: "", description: "" }]);
  }

  function removeBenefit(index) {
    setBenefits((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("submitting");
    setError(null);

    const form = new FormData(event.currentTarget);

    const fields = {
      title: form.get("title"),
      slug: form.get("slug"),
      author_name: form.get("authorName"),
      author_title: form.get("authorTitle"),
      published: form.get("published") === "on",
      headline: form.get("headline"),
      subheadline: form.get("subheadline"),
      category: form.get("category"),
      intro_body: form.get("introBody"),
      closing_heading: form.get("closingHeading"),
      closing_body: form.get("closingBody"),
      contact_phone: form.get("contactPhone") || null,
      contact_email: form.get("contactEmail") || null,
      benefits: benefits.filter((b) => b.title.trim() || b.description.trim()),
    };

    const files = {
      pdf: form.get("pdf")?.size ? form.get("pdf") : null,
      cover: form.get("cover")?.size ? form.get("cover") : null,
      preview: form.get("preview")?.size ? form.get("preview") : null,
    };

    const { error } =
      mode === "create"
        ? await createBook(fields, files)
        : await updateBook(book.id, fields, files, book);

    if (error) {
      setError(error.message ?? "Something went wrong.");
      setStatus("error");
      return;
    }

    navigate("/admin/books-portal");
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold text-zinc-900">
        {mode === "create" ? "Add a Book" : `Edit "${book.title}"`}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 rounded-lg border border-zinc-200 bg-white p-6"
      >
        <Section title="Basics">
          <Field label="Title" name="title" required defaultValue={book?.title} />
          <Field
            label="Slug (URL path, e.g. home-buyers-guide)"
            name="slug"
            required
            defaultValue={book?.slug}
            hint={
              mode === "edit"
                ? "Changing this changes the book's public URL — any links already shared with the old slug will stop working."
                : undefined
            }
          />
          <Field
            label="Author Name"
            name="authorName"
            defaultValue={book?.author_name ?? "Sumit Mehra"}
          />
          <Field
            label="Author Title"
            name="authorTitle"
            defaultValue={book?.author_title ?? "Realtor & Author"}
          />
          <label className="flex items-center gap-2 text-sm font-medium text-zinc-800">
            <input
              type="checkbox"
              name="published"
              defaultChecked={book?.published ?? true}
              className="h-4 w-4 rounded border-zinc-300"
            />
            Published (visible to the public)
          </label>
        </Section>

        <Section title="Hero section">
          <Field
            label="Headline"
            name="headline"
            defaultValue={book?.headline ?? "FREE Book Reveals How To Buy Your Dream Home"}
          />
          <TextArea
            label="Subheadline"
            name="subheadline"
            defaultValue={book?.subheadline}
          />
        </Section>

        <Section title="What's inside (benefit cards)">
          <Field
            label="Category (powers the '#1 in ___' badge above this section)"
            name="category"
            defaultValue={book?.category ?? "Home Buying"}
          />
          <TextArea
            label="Intro paragraphs (shown before the sneak-peek preview image)"
            name="introBody"
            rows={5}
            defaultValue={book?.intro_body}
          />
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className="space-y-2 rounded-md border border-zinc-200 p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-zinc-500">
                  Card {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeBenefit(i)}
                  className="text-xs font-medium text-red-600 hover:underline"
                >
                  Remove
                </button>
              </div>
              <input
                value={benefit.title}
                onChange={(e) => updateBenefit(i, "title", e.target.value)}
                placeholder="Card title"
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
              />
              <textarea
                value={benefit.description}
                onChange={(e) => updateBenefit(i, "description", e.target.value)}
                placeholder="Card description"
                rows={2}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
              />
              <select
                value={benefit.icon ?? ""}
                onChange={(e) => updateBenefit(i, "icon", e.target.value)}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm"
              >
                <option value="">Icon: auto (rotates by position)</option>
                {BENEFIT_ICON_KEYS.map((key) => (
                  <option key={key} value={key}>
                    Icon: {key}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <button
            type="button"
            onClick={addBenefit}
            className="text-sm font-medium text-sky-600 hover:underline"
          >
            + Add another card
          </button>
        </Section>

        <Section title="Closing section">
          <Field
            label="Closing Heading"
            name="closingHeading"
            defaultValue={book?.closing_heading}
          />
          <TextArea
            label="Closing Body"
            name="closingBody"
            rows={4}
            defaultValue={book?.closing_body}
          />
        </Section>

        <Section title="Contact (optional — shows a 'Get In Touch' section)">
          <Field label="Phone" name="contactPhone" defaultValue={book?.contact_phone ?? ""} />
          <Field label="Email" name="contactEmail" defaultValue={book?.contact_email ?? ""} />
        </Section>

        <Section title="Files">
          <FileField
            label="PDF File"
            name="pdf"
            accept="application/pdf"
            required={mode === "create"}
            hint={
              mode === "edit"
                ? "Leave blank to keep the current PDF."
                : undefined
            }
          />
          <FileField
            label="Cover Image"
            name="cover"
            accept="image/*"
            currentUrl={book?.cover_image_url}
            hint="Shown in the hero and closing sections."
          />
          <FileField
            label="Preview Image"
            name="preview"
            accept="image/*"
            currentUrl={book?.preview_image_url}
            hint="A 'sneak peek inside' screenshot shown between the hero and the benefit cards."
          />
        </Section>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full rounded-md bg-zinc-900 px-4 py-2 font-semibold text-white hover:bg-zinc-800 disabled:opacity-60"
        >
          {status === "submitting"
            ? "Saving…"
            : mode === "create"
              ? "Create Book"
              : "Save Changes"}
        </button>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="space-y-3 border-t border-zinc-100 pt-5 first:border-t-0 first:pt-0">
      <legend className="mb-2 text-sm font-bold uppercase tracking-wide text-zinc-500">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Field({ label, name, required, defaultValue, disabled, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-zinc-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        name={name}
        required={required}
        defaultValue={defaultValue}
        disabled={disabled}
        className="w-full rounded-md border border-zinc-300 px-3 py-2 disabled:bg-zinc-100 disabled:text-zinc-500"
      />
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

function TextArea({ label, name, rows = 2, defaultValue }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-zinc-800">
        {label}
      </label>
      <textarea
        name={name}
        rows={rows}
        defaultValue={defaultValue}
        className="w-full rounded-md border border-zinc-300 px-3 py-2"
      />
    </div>
  );
}

function FileField({ label, name, accept, required, currentUrl, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-zinc-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {currentUrl && (
        <img
          src={currentUrl}
          alt=""
          className="mb-2 h-20 w-auto rounded border border-zinc-200 object-contain"
        />
      )}
      <input
        type="file"
        name={name}
        accept={accept}
        required={required}
        className="w-full rounded-md border border-zinc-300 px-3 py-2"
      />
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}
